import React from "react";
import cn from "classnames";
import { twMerge } from "tailwind-merge";
import { toFarsiNumber } from "@utils/digits";

const styleMode = {
  primary:
    "bg-primary hover:bg-primaryHover text-white disabled:bg-[#A5CFF5]  disabled:text-zinc-50  ",
  "primary-new":
    "bg-[#061B2E] hover:bg-[#01060a] text-white disabled:bg-[#F4F4F5] disabled:hover:bg-[#F4F4F5] disabled:text-[#D4D4D8] disabled:cursor-not-allowed ",
  secondary: "bg-lightBlue hover:bg-lightBlueHover text-white",
  outline:
    "bg-transparent hover:bg-lightBlue2 text-primary border border-primary",
  dangerOutline:
    "bg-transparent hover:bg-red-500 text-red-500 hover:text-white  border border-red-500 disabled:border-[#FCA5A5] disabled:text-[#FCA5A5]",
  secondOutline:
    "bg-transparent hover:bg-cyan text-lightBlue hover:text-black  border border-lightBlue",
  noneStyle: "text-black font-medium ",
  danger: "bg-red-600  hover:bg-dangerHover text-white",
  success: "bg-success hover:bg-success text-white",
  defaultOutline:
    "bg-transparent hover:bg-[rgba(0,0,0,.1)] text-black    border border-[rgb(217 214 214)]",
  cyan: "bg-[#27aae1] hover:bg-[#27AAE1] text-white",
  blue: "bg-[#1E88E5] text-white",
  "primary-new-outline":
    "bg-transparent  text-[#061B2E] border border-[#061B2E]   disabled:border-[#F4F4F5] disabled:hover:border-[#F4F4F5] disabled:text-[#D4D4D8] disabled:cursor-not-allowed ",
  darkOutline:
    "border border-black text-black hover:bg-black rounded-lg hover:text-white  text-sm !font-normal",
  dark: "bg-black text-white disabled:bg-zinc-100 disabled:text-zinc-300 rounded-lg h-12 ",
  tertiaryFill:
    "bg-zinc-100 hover:bg-zinc-200 text-zinc-600 disabled:bg-zinc-50 disabled:text-zinc-300 focused:bg-zinc-100",
  primaryOutline:
    "bg-transparent border border-[#1E88E5] text-[#1E88E5] hover:bg-[#E9F3FC] hover:border-[#186DB7] hover:text-[#186DB7] focused:bg-[#BCDBF7] focused:border-[#125289]",
  default:
    "bg-transparent hover:bg-zinc-800 hover:text-white text-zinc-800 rounded-md border border-zinc-900 text-zinc-900 focused:border-zinc-400 focused:text-zinc-800 disabled:border-zinc-200 disabled:text-zinc-200",
  secondDanger:
    "bg-[#F8DDE0] text-[#BF2938] hover:text-[#95202B]  hover:bg-[#F1BEC3]",
};

const sizeBtn = {
  small: "p-[0.75rem_0.9rem]",
  medium: "p-[8px_18px]",
  large: "p-[0.7rem_0.4rem] min-w-[190px] justify-center",
};

const alignBtn = {
  center: "flex justify-center items-center",
  left: "flex items-start",
  right: "flex item-end",
};
export interface ButtonProps
  extends Omit<React.HTMLProps<HTMLButtonElement>, "size"> {
  mode:
    | "blue"
    | "primary"
    | "primary-new"
    | "outline"
    | "noneStyle"
    | "success"
    | "danger"
    | "dangerOutline"
    | "secondary"
    | "secondOutline"
    | "defaultOutline"
    | "cyan"
    | "primary-new-outline"
    | "darkOutline"
    | "dark"
    | "tertiaryFill"
    | "default"
    | "primaryOutline"
    | "secondDanger";
  size?: "small" | "medium" | "large";
  label?: string;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  iconPosition?: "left" | "right";
  className?: string;
  vertical?: boolean;
  type?: "submit" | "reset" | "button";
  isBadge?: boolean;
  badge?: number;
  badgeClassName?: string;
  align?: "center" | "left" | "right";
}

const Button: React.FC<ButtonProps> = ({
  mode = "primary",
  size = "medium",
  label = "",
  iconPosition,
  icon,
  className = "",
  children,
  vertical = false,
  type = "submit",
  badge,
  isBadge,
  badgeClassName,
  disabled,
  align = "center",
  ...props
}: ButtonProps) => {
  return (
    <button
      type={type}
      aria-label={props["aria-label"] ? props["aria-label"] : "Button"}
      className={twMerge(
        "flex items-center text-sm font-medium",
        isBadge ? "relative" : "",
        label === "" ? "justify-center" : "",
        iconPosition === "left"
          ? "flex-row"
          : vertical
            ? "flex-col-reverse "
            : "flex-row-reverse ",
        styleMode[mode],
        sizeBtn[size],
        alignBtn[align],
        disabled && "cursor-not-allowed",
        className
      )}
      disabled={disabled}
      {...props}
    >
      {label && <span>{label}</span>}
      {icon}
      {isBadge ? (
        <span
          className={cn(
            "absolute bottom-0 h-max border-[2px] border-[white] inline-flex items-center justify-center rounded-full bg-primary px-2 pb-1 pt-1.5 text-xs font-bold  leading-none text-white",
            badgeClassName
          )}
        >
          {toFarsiNumber(String(badge))}
        </span>
      ) : (
        ""
      )}
      {children}
    </button>
  );
};
export default Button;
